import { type SQL as DrizzleSql, sql as drizzleSql } from "drizzle-orm";

import { bindSqlValue } from "../../../query/dialect/profile";
import { type SqlDialect } from "../../../query/dialect/types";
import {
  isSqlFragment,
  renderSql,
  type SqlFragment,
  throwUnsupportedSqlChunk,
} from "../../../query/sql-fragment";
import type {
  AtomicSqlBatchExecutor,
  CompiledAtomicSqlStatement,
} from "../../capabilities/atomic-sql-program";

export type ExecutableSql = DrizzleSql | SqlFragment;

export type CompiledSqlQuery = CompiledAtomicSqlStatement;

/**
 * A driver-native, all-or-nothing group of compiled statements.
 *
 * This is intentionally an optional execution surface. Most SQL drivers only
 * expose one-statement execution through Drizzle; drivers with a native batch
 * primitive can provide it without making callers guess whether a sequence is
 * actually transactional. The static name is deliberate: this hook submits a
 * complete batch to a native primitive and is not an interactive transaction
 * runner.
 */
export type PreparedSqlStatement = Readonly<{
  execute: <TRow>(params: readonly unknown[]) => Promise<readonly TRow[]>;
}>;

export type SqlExecutionAdapter = Readonly<{
  compile: (query: ExecutableSql) => CompiledSqlQuery;
  execute: <TRow>(query: ExecutableSql) => Promise<readonly TRow[]>;
  executeCompiled?: <TRow>(
    compiledQuery: CompiledSqlQuery,
  ) => Promise<readonly TRow[]>;
  /** Executes all statements atomically when the native driver supports it. */
  executeAtomicBatch?: AtomicSqlBatchExecutor;
  prepare?: (sqlText: string) => PreparedSqlStatement;
  /**
   * Runs `critical` with exclusive use of the connection: no statement from
   * anywhere else can interleave with the ones it issues.
   *
   * Statement-at-a-time serialization is not enough for a *sequence* that must
   * be atomic. `SET LOCAL` around a query is the motivating case — snapshot,
   * set, select, restore. Two searches whose statements merely take turns can
   * still interleave as `A snapshot → B snapshot → A set → B set → A select`,
   * and `A` then runs under `B`'s settings.
   *
   * `critical` is handed the unqueued adapter, so it must not attempt to
   * re-enter the queue. Present only on transaction-scoped (serialized)
   * adapters; a pooled adapter needs no exclusion because every statement gets
   * its own connection.
   */
  runExclusive?: <T>(
    critical: (connection: SqlExecutionAdapter) => Promise<T>,
  ) => Promise<T>;
  /**
   * Recognizes this engine's own commit-conflict shape when it is not
   * PostgreSQL's `40001`/`40P01` SQLSTATE (or the fixed message fallback for
   * a driver that drops the code). `createSqlBackend` registers this
   * classifier against the exact backend object it returns; `isSerializationFailure`
   * (`src/utils/sql-errors.ts`) consults it before falling back to its own
   * SQLSTATE/message rules, so there remains ONE predicate every retry owner
   * calls, never a second inline check for engines this covers.
   */
  serializationFailure?: (error: unknown) => boolean;
}>;

type SqlCompiler = Readonly<{
  sqlToQuery: (query: DrizzleSql) => CompiledSqlQuery;
}>;

type DatabaseWithCompiler = Readonly<{
  dialect?: SqlCompiler;
}>;

type BackendName = "PostgreSQL" | "SQLite";

const DIALECT_BY_BACKEND_NAME = {
  PostgreSQL: "postgres",
  SQLite: "sqlite",
} as const satisfies Record<BackendName, SqlDialect>;

export function compileQueryWithDialect(
  db: unknown,
  query: ExecutableSql,
  backendName: BackendName,
): CompiledSqlQuery {
  if (isSqlFragment(query)) {
    return renderSql(query, DIALECT_BY_BACKEND_NAME[backendName]);
  }
  const databaseWithCompiler = db as DatabaseWithCompiler;
  const compiler = databaseWithCompiler.dialect;
  if (compiler === undefined) {
    throw new Error(`${backendName} backend is missing a SQL compiler`);
  }
  return compiler.sqlToQuery(query);
}

/**
 * Translates TypeGraph's fragment IR into Drizzle's adapter-native SQL object.
 *
 * Most execution paths render directly to text and parameters. Async SQLite
 * drivers expose only Drizzle's execution method, so that adapter needs this
 * lossless bridge at its final boundary.
 */
export function toDrizzleSql(
  fragment: SqlFragment,
  dialect: SqlDialect,
): DrizzleSql {
  const chunks = fragment.chunks.map((chunk) => {
    switch (chunk.kind) {
      case "text": {
        return drizzleSql.raw(chunk.value);
      }
      case "identifier": {
        // Drizzle's schema builders treat Name chunks as bare index-column
        // references and omit dialect quoting. Preserve the portable IR's
        // identifier semantics by handing the adapter an already-quoted,
        // trusted SQL token instead.
        return drizzleSql.raw(`"${chunk.value.replaceAll('"', '""')}"`);
      }
      case "parameter": {
        return drizzleSql.param(bindSqlValue(chunk.value, dialect));
      }
      case "placeholder": {
        return drizzleSql.placeholder(chunk.value.name);
      }
      default: {
        return throwUnsupportedSqlChunk(chunk);
      }
    }
  });
  return drizzleSql.join(chunks);
}
